import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Button, Tag } from "../../../../../design-system";
import { MediaProject } from "../../../../../types/MediaProject";
import {
  approveScheduledPost,
  cancelScheduledPost,
  retryScheduledPost,
  restoreScheduledPost,
  updateScheduledPost,
  ScheduledPost,
} from "../../../../../database/community/scheduledPosts";
import { platformLabel } from "../../publish/SchedulePostDialog";
import { useMediaStudio } from "../../context/MediaStudioContext";
import {
  SectionRoot,
  SectionTitle,
  SectionSub,
  Muted,
  Spacer,
  STATUS_COLOR,
  projectThumb,
} from "../shared";

// Month calendar over the owned scheduler queue. Each day cell shows the posts
// due that day; clicking one opens a detail panel with the full content and
// the actions for that status. Cancelled posts can be surfaced with the toggle
// and restored back into review.

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const pad = (n: number) => String(n).padStart(2, "0");
const localDayKey = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const timeOf = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

const isoToLocalInput = (iso: string | null): string => {
  const d = iso ? new Date(iso) : new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const buildMonthCells = (year: number, month: number): Date[] => {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(year, month, 1 - startWeekday + i));
  }
  return cells;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
`;

const MonthLabel = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  min-width: 168px;
`;

const NavButton = styled.button`
  appearance: none;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  width: 30px;
  height: 30px;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 15px;
  line-height: 1;

  &:hover {
    border-color: var(--color-accent);
    color: var(--color-accent);
  }
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
`;

const WeekHead = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
`;

const WeekHeadCell = styled.div`
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-tertiary);
  padding: 6px 8px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: var(--color-border-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  overflow: hidden;
`;

const DayCell = styled.div<{ $muted: boolean; $today: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-height: 104px;
  padding: 6px;
  background: ${(p) =>
    p.$muted ? "var(--color-surface-subtle)" : "var(--color-surface)"};
  outline: ${(p) => (p.$today ? "2px solid var(--color-accent)" : "none")};
  outline-offset: -2px;
`;

const DayNumber = styled.div<{ $muted: boolean }>`
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: ${(p) =>
    p.$muted ? "var(--color-text-tertiary)" : "var(--color-text)"};
`;

const Pill = styled.button<{ $cancelled: boolean }>`
  appearance: none;
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
  border: none;
  background: var(--color-surface-subtle);
  border-radius: var(--radius-sm, 6px);
  padding: 3px 6px;
  cursor: pointer;
  text-align: left;
  opacity: ${(p) => (p.$cancelled ? 0.55 : 1)};

  &:hover {
    background: var(--color-border-subtle);
  }
`;

const PillDot = styled.span<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  flex-shrink: 0;
`;

const PillText = styled.span<{ $strike: boolean }>`
  font-family: var(--font-sans);
  font-size: 10px;
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: ${(p) => (p.$strike ? "line-through" : "none")};
`;

const Unscheduled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--space-3);
  border: 1px dashed var(--color-border-subtle);
  border-radius: var(--radius-lg);
`;

const UnscheduledRow = styled.button`
  appearance: none;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  padding: 4px 0;
`;

// ─── Detail modal ────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: var(--space-6) var(--space-3);
  overflow-y: auto;
  z-index: 40;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 560px;
  background: var(--color-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg, var(--shadow-md));
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const ModalHead = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
`;

const CloseButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: var(--color-text);
  }
`;

const MediaWrap = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  border-radius: var(--radius-lg);

  img,
  video {
    width: 96px;
    height: 120px;
    object-fit: cover;
    border-radius: var(--radius-md);
    background: var(--color-surface-subtle);
  }
`;

const CaptionBox = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: 1.5;
  color: var(--color-text);
  white-space: pre-wrap;
  max-height: 220px;
  overflow-y: auto;
  padding: var(--space-2) var(--space-3);
  background: var(--color-surface-subtle);
  border-radius: var(--radius-md);
`;

const MetaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetaLabel = styled.span`
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-tertiary);
`;

const MetaValue = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--color-text-secondary);
`;

const ResultLink = styled.a`
  color: var(--color-accent-text, var(--color-accent));
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
  border-top: 1px solid var(--color-border-subtle);
  padding-top: var(--space-3);
`;

const WhenInput = styled.input`
  appearance: none;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  padding: 6px 10px;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
`;

const statusTagVariant = (status: string) => {
  if (status === "draft") return "warning" as const;
  if (status === "sent") return "success" as const;
  if (status === "error" || status === "cancelled") return "danger" as const;
  if (status === "partial") return "warning" as const;
  return "info" as const;
};

const PostDetail: React.FC<{
  post: ScheduledPost;
  project?: MediaProject;
  communityId?: string;
  onClose: () => void;
}> = ({ post, project, communityId, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [when, setWhen] = useState(isoToLocalInput(post.dueAt));

  const act = async (fn: () => Promise<void>, close = false) => {
    if (!communityId) return;
    setBusy(true);
    try {
      await fn();
      if (close) onClose();
    } catch (e) {
      console.error("Post action failed:", e);
    } finally {
      setBusy(false);
    }
  };

  const slides = project?.slides || [];
  const media: React.ReactNode = (() => {
    if (project?.kind === "carousel" && slides.length > 0) {
      return slides.map((s, i) => <img key={i} src={s.mediaUrl} alt="" />);
    }
    if (project?.kind === "reel" && project.outputUrl) {
      return <video src={project.outputUrl} controls preload="metadata" playsInline />;
    }
    if (post.mediaUrls.length > 0) {
      return post.mediaUrls.map((u, i) => <img key={i} src={u} alt="" />);
    }
    if (project?.thumbnailUrl) {
      return <img src={project.thumbnailUrl} alt="" />;
    }
    return null;
  })();

  const results = post.results ? Object.entries(post.results) : [];

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <Tag variant={statusTagVariant(post.status)} size="sm" withDot>
            {post.status}
          </Tag>
          {project?.title ? (
            <MetaValue style={{ fontWeight: 600 }}>{project.title}</MetaValue>
          ) : (
            <MetaValue style={{ fontWeight: 600 }}>
              {project?.kind === "reel" ? "Reel" : "Post"}
            </MetaValue>
          )}
          <Spacer />
          <CloseButton type="button" aria-label="Close" onClick={onClose}>
            ×
          </CloseButton>
        </ModalHead>

        {media ? <MediaWrap>{media}</MediaWrap> : null}

        <CaptionBox>
          {post.caption?.trim() ? post.caption : "No caption on this post."}
        </CaptionBox>

        <MetaGrid>
          <MetaItem>
            <MetaLabel>Platforms</MetaLabel>
            <MetaValue>
              {post.platforms.length
                ? post.platforms.map(platformLabel).join(", ")
                : "None"}
            </MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>{post.status === "sent" ? "Sent" : "Goes out"}</MetaLabel>
            <MetaValue>
              {post.dueAt ? new Date(post.dueAt).toLocaleString() : "No date set"}
            </MetaValue>
          </MetaItem>
          {post.attempts > 0 && (
            <MetaItem>
              <MetaLabel>Attempts</MetaLabel>
              <MetaValue>{post.attempts}</MetaValue>
            </MetaItem>
          )}
        </MetaGrid>

        {results.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <MetaLabel>Results</MetaLabel>
            {results.map(([platform, r]) => (
              <ResultRow key={platform}>
                <strong style={{ textTransform: "capitalize" }}>
                  {platformLabel(platform)}
                </strong>
                {r.error ? (
                  <span style={{ color: "var(--color-danger)" }}>{r.error}</span>
                ) : r.url ? (
                  <ResultLink href={r.url} target="_blank" rel="noopener noreferrer">
                    View post
                  </ResultLink>
                ) : (
                  <span>{r.via ? `via ${r.via}` : "sent"}</span>
                )}
              </ResultRow>
            ))}
          </div>
        )}

        {communityId && (
          <Actions>
            {rescheduling ? (
              <>
                <WhenInput
                  type="datetime-local"
                  value={when}
                  onChange={(e) => setWhen(e.target.value)}
                />
                <Button
                  variant="primary"
                  size="sm"
                  isLoading={busy}
                  onClick={() =>
                    act(async () => {
                      await updateScheduledPost(communityId, post.id, {
                        dueAt: new Date(when).toISOString(),
                      });
                      setRescheduling(false);
                    })
                  }
                >
                  Save time
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => setRescheduling(false)}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {post.status === "draft" && (
                  <Button
                    variant="primary"
                    size="sm"
                    isLoading={busy}
                    onClick={() =>
                      act(() => approveScheduledPost(communityId, post.id), true)
                    }
                  >
                    Approve
                  </Button>
                )}
                {(post.status === "error" || post.status === "partial") && (
                  <Button
                    variant="primary"
                    size="sm"
                    isLoading={busy}
                    onClick={() =>
                      act(() => retryScheduledPost(communityId, post.id), true)
                    }
                  >
                    Retry
                  </Button>
                )}
                {post.status === "cancelled" && (
                  <Button
                    variant="primary"
                    size="sm"
                    isLoading={busy}
                    onClick={() =>
                      act(() => restoreScheduledPost(communityId, post.id), true)
                    }
                  >
                    Restore to review
                  </Button>
                )}
                {(post.status === "draft" || post.status === "scheduled") && (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => setRescheduling(true)}
                  >
                    Reschedule
                  </Button>
                )}
                {(post.status === "draft" ||
                  post.status === "scheduled" ||
                  post.status === "error" ||
                  post.status === "partial") && (
                  <Button
                    variant="danger"
                    size="sm"
                    isLoading={busy}
                    onClick={() =>
                      act(() => cancelScheduledPost(communityId, post.id), true)
                    }
                  >
                    Cancel post
                  </Button>
                )}
              </>
            )}
          </Actions>
        )}
      </Modal>
    </Overlay>
  );
};

// ─── Calendar ────────────────────────────────────────────────────────────────

export type CalendarQueueProps = {
  onNavigate: (path: string) => void;
};

export const CalendarQueue: React.FC<CalendarQueueProps> = ({ onNavigate }) => {
  const { communityId, posts, projects } = useMediaStudio();
  const now = new Date();
  const [cursor, setCursor] = useState({
    year: now.getFullYear(),
    month: now.getMonth(),
  });
  const [showCancelled, setShowCancelled] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const projectFor = (p: ScheduledPost) =>
    p.projectId ? projects.projectById.get(p.projectId) : undefined;
  const thumbFor = (p: ScheduledPost) => projectThumb(projectFor(p));

  // Posts shown on the grid: everything with a date, minus cancelled unless the
  // toggle is on. Unscheduled (no dueAt) render in their own strip below.
  const visible = useMemo(
    () =>
      posts.posts.filter(
        (p) => showCancelled || p.status !== "cancelled"
      ),
    [posts.posts, showCancelled]
  );

  const byDay = useMemo(() => {
    const map = new Map<string, ScheduledPost[]>();
    visible.forEach((p) => {
      if (!p.dueAt) return;
      const key = localDayKey(new Date(p.dueAt));
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    });
    map.forEach((arr) =>
      arr.sort((a, b) => (a.dueAt || "").localeCompare(b.dueAt || ""))
    );
    return map;
  }, [visible]);

  const unscheduled = useMemo(
    () => visible.filter((p) => !p.dueAt),
    [visible]
  );

  const cells = useMemo(
    () => buildMonthCells(cursor.year, cursor.month),
    [cursor]
  );
  const todayKey = localDayKey(now);
  const monthLabel = new Date(cursor.year, cursor.month, 1).toLocaleDateString(
    [],
    { month: "long", year: "numeric" }
  );

  const shiftMonth = (delta: number) =>
    setCursor((c) => {
      const d = new Date(c.year, c.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });

  const selected = selectedId
    ? posts.posts.find((p) => p.id === selectedId) || null
    : null;

  return (
    <SectionRoot>
      <div>
        <SectionTitle>Calendar</SectionTitle>
        <SectionSub>
          Everything queued to go out. Click a post to see what is inside and
          act on it. Drafts still need approval in Review before they publish.
        </SectionSub>
      </div>

      <Toolbar>
        <NavButton type="button" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
          ‹
        </NavButton>
        <MonthLabel>{monthLabel}</MonthLabel>
        <NavButton type="button" aria-label="Next month" onClick={() => shiftMonth(1)}>
          ›
        </NavButton>
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            setCursor({ year: now.getFullYear(), month: now.getMonth() })
          }
        >
          Today
        </Button>
        <Spacer />
        {posts.cancelled.length > 0 && (
          <ToggleLabel>
            <input
              type="checkbox"
              checked={showCancelled}
              onChange={(e) => setShowCancelled(e.target.checked)}
            />
            Show cancelled ({posts.cancelled.length})
          </ToggleLabel>
        )}
      </Toolbar>

      <div>
        <WeekHead>
          {WEEKDAYS.map((w) => (
            <WeekHeadCell key={w}>{w}</WeekHeadCell>
          ))}
        </WeekHead>
        <Grid>
          {cells.map((d) => {
            const key = localDayKey(d);
            const dayPosts = byDay.get(key) || [];
            const muted = d.getMonth() !== cursor.month;
            // One line per platform: a post going to TikTok and Instagram shows
            // as two separate lines. Posts with no platform still get one line.
            const lines = dayPosts.flatMap((p) =>
              p.platforms.length
                ? p.platforms.map((platform) => ({ post: p, platform }))
                : [{ post: p, platform: null as string | null }]
            );
            return (
              <DayCell key={key} $muted={muted} $today={key === todayKey}>
                <DayNumber $muted={muted}>{d.getDate()}</DayNumber>
                {lines.slice(0, 4).map((line, i) => (
                  <Pill
                    key={`${line.post.id}-${line.platform ?? i}`}
                    type="button"
                    $cancelled={line.post.status === "cancelled"}
                    title={line.post.caption || line.post.status}
                    onClick={() => setSelectedId(line.post.id)}
                  >
                    <PillDot
                      $color={
                        STATUS_COLOR[line.post.status] || "var(--color-text-tertiary)"
                      }
                    />
                    <PillText $strike={line.post.status === "cancelled"}>
                      {timeOf(line.post.dueAt)}
                      {line.platform ? ` ${platformLabel(line.platform)}` : ""}
                    </PillText>
                  </Pill>
                ))}
                {lines.length > 4 && (
                  <Muted style={{ fontSize: 10 }}>+{lines.length - 4} more</Muted>
                )}
              </DayCell>
            );
          })}
        </Grid>
      </div>

      {unscheduled.length > 0 && (
        <Unscheduled>
          <MetaLabel>No date set</MetaLabel>
          {unscheduled.map((p) => (
            <UnscheduledRow
              key={p.id}
              type="button"
              onClick={() => setSelectedId(p.id)}
            >
              <PillDot
                $color={STATUS_COLOR[p.status] || "var(--color-text-tertiary)"}
              />
              {thumbFor(p) ? (
                <img
                  src={thumbFor(p)}
                  alt=""
                  style={{ width: 24, height: 30, objectFit: "cover", borderRadius: 4 }}
                />
              ) : null}
              <Muted style={{ color: "var(--color-text)" }}>{p.status}</Muted>
              <Muted>{p.platforms.map(platformLabel).join(", ")}</Muted>
              {p.caption ? (
                <Muted
                  style={{
                    maxWidth: 260,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.caption}
                </Muted>
              ) : null}
            </UnscheduledRow>
          ))}
        </Unscheduled>
      )}

      {posts.posts.length === 0 && (
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onNavigate("/media-studio/library")}
          >
            Schedule from library
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onNavigate("/media-studio/create")}
          >
            Create content
          </Button>
        </div>
      )}

      {selected && (
        <PostDetail
          post={selected}
          project={projectFor(selected)}
          communityId={communityId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </SectionRoot>
  );
};

export default CalendarQueue;
